import { translate, getLocale } from 'shared/lib/i18n.tsx';

import type { Difficulty, Pose } from '../../../games/shared/contract.ts';

import { parseIqFrame, type IqFrame } from './iqFrame.ts';
import {
  setupChanged, startsAfterRebuild, transition, type IqWebPhase,
} from './iqWebPhase.ts';
import { drawUpperBodyCalibration } from './upperBodyCalibration.ts';

/**
 * IQ на ТВ (GEN-19048, комната `iq-web`). Правила и отрисовка — та же страница, что и у телефона
 * (`src/games/iq`, сборка в `dist/client/games/iq`), только в iframe 16:9 с `?platform=tv`. Телефон —
 * камера и контроллер: выбирает тему и сложность, калибруется сам, считает позу и шлёт кадры
 * `{type: 'IqData', gameState, topicId, difficulty, detectionProgress, pose}` ~30 раз/с; ТВ передаёт позу в
 * `grooveSetPose`. Итог (очки, верные, всего, время) страница отдаёт сообщением `finished` → `onFinished` →
 * `game:state gameover`; в сессию его пишет телефон.
 *
 * Тема и сложность приходят в кадрах, а страница читает их при загрузке — поэтому iframe поднимается по первому
 * кадру и собирается заново, если телефон прислал другой выбор. Пауза, возобновление и рестарт идут полем
 * `gameState` кадров (канал без гарантий доставки: телефон повторяет кадры состояния; повторы здесь безвредны,
 * действие выполняется только при смене фазы).
 */
export type { IqWebPhase };

export interface IqWebResult {
  score: number;
  correct: number;
  total: number;
  durationSec: number;
}

/** Методы страницы (контракт веб-игр v1). */
interface IqPageApi {
  grooveStart(): void;
  groovePause(): void;
  grooveResume(): void;
  grooveRestart(): void;
  grooveSetPose(pose: Pose): void;
}

const Z_INDEX = '1';
const PAGE_URL = '/pages/games/iq/index.html';
/** Цвет страницы IQ: фон хоста и iframe до первого кадра страницы. */
const PAGE_COLOR = '#1a1552';
/** Страница должна отозваться `ready` (вопросы загружены) за это время, иначе показываем ошибку. */
const LOAD_TIMEOUT_MS = 15_000;

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

export class IqWebDisplayEngine {
  private phase: IqWebPhase = 'lobby';

  private host: HTMLDivElement | null = null;

  private frame: HTMLIFrameElement | null = null;

  private statusEl: HTMLDivElement | null = null;

  private calibCanvas: HTMLCanvasElement | null = null;

  private calibRaf = 0;

  /** Заполнение рамки на телефоне, 0…1. */
  private detectionProgress = 0;

  /** Выбор с телефона; до первого кадра страницу не поднимаем (`setupKnown`), иначе вопросы не те. */
  private setupKnown = false;

  private topicId: number | null = null;

  private difficulty: Difficulty = 'EASY';

  private pageReady = false;

  /** Страница не загрузилась (`error` до `ready` или таймаут): плашка остаётся, пока страницу не пересоберут. */
  private loadFailed = false;

  /** `start-game` пришёл, `grooveStart()` ещё не вызван (страница не успела загрузиться). */
  private startPending = false;

  private pageStarted = false;

  /** После последнего (ре)старта приходили кадры `STARTED`. */
  private sawStarted = false;

  private loadTimer = 0;

  onFinished?: (result: IqWebResult) => void;

  getPhase(): IqWebPhase {
    return this.phase;
  }

  /** `select-game` и рестарт с телефона: калибровка на телефоне, ТВ рисует рамку по её прогрессу. */
  startCalibration(): void {
    this.ensureHost();
    if (this.frame) this.api()?.grooveRestart();
    this.startPending = false;
    this.pageStarted = false;
    this.sawStarted = false;
    this.detectionProgress = 0;
    this.phase = 'calibration';
    this.showStatus(null);
    this.showCalibration(true);
  }

  /** `start-game` с телефона: калибровка пройдена, начинаем раунд (страница ещё грузится — стартуем по `ready`). */
  startGame(): void {
    this.ensureHost();
    this.phase = 'playing';
    this.sawStarted = false;
    this.showCalibration(false);
    this.showStatus(this.loadFailed ? translate('game.chef_load_failed') : null);
    this.ensurePage();
    this.startPage();
  }

  /**
   * Телефон отвалился посреди раунда: пока поверх экран разрыва, раунд стоит. Вернётся телефон — кадры `STARTED`
   * снимут паузу; не вернётся — ТВ уйдёт в лобби и `reset()` уберёт страницу.
   */
  pauseForDisconnect(): void {
    if (this.phase !== 'playing') return;
    this.api()?.groovePause();
    this.phase = 'paused';
  }

  onGameFrame(rawFrame: unknown): void {
    const frame = parseIqFrame(rawFrame);
    if (!frame || this.phase === 'lobby') return;
    this.applySetup(frame);

    // Рестарт с телефона (после паузы или итога) — снова калибровка; решает `transition`.
    const next = transition(this.phase, this.sawStarted, frame.gameState);
    switch (next.step) {
    case 'restart':
      this.startCalibration();
      if (frame.detectionProgress !== null) this.detectionProgress = frame.detectionProgress;
      break;
    case 'calibrate':
      if (frame.detectionProgress !== null) this.detectionProgress = frame.detectionProgress;
      break;
    case 'pause':
      this.api()?.groovePause();
      this.phase = next.phase;
      this.showStatus(translate('game.pause'));
      break;
    case 'resume':
      this.sawStarted = next.sawStarted;
      this.api()?.grooveResume();
      this.phase = next.phase;
      this.showStatus(null);
      if (frame.pose) this.api()?.grooveSetPose(frame.pose);
      break;
    case 'play':
      this.sawStarted = next.sawStarted;
      if (frame.pose) this.api()?.grooveSetPose(frame.pose);
      break;
    default:
      break;
    }
  }

  reset(): void {
    this.phase = 'lobby';
    this.setupKnown = false;
    this.showCalibration(false);
    this.destroyPage();
    window.removeEventListener('resize', this.fit);
    this.host?.remove();
    this.host = null;
    this.statusEl = null;
    this.calibCanvas = null;
  }

  /** Контейнер, статус и канва калибровки — дёшево, без страницы. */
  private ensureHost(): void {
    if (this.host) return;
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;inset:0;z-index:${Z_INDEX};background:${PAGE_COLOR};`;
    document.body.appendChild(host);

    const calib = document.createElement('canvas');
    calib.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:2;display:none;';
    host.appendChild(calib);

    const status = document.createElement('div');
    status.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:3;'
      + 'padding:24px 40px;border-radius:24px;background:rgba(20,14,50,.85);color:#fff;'
      + 'font:800 40px -apple-system,system-ui,sans-serif;text-align:center;display:none;';
    host.appendChild(status);

    this.host = host;
    this.calibCanvas = calib;
    this.statusEl = status;
    window.addEventListener('resize', this.fit);
  }

  /** Страница под выбор с телефона; если она уже есть или выбора ещё нет — остаётся как есть. */
  private ensurePage(): void {
    if (this.frame || !this.host || !this.setupKnown) return;
    const frame = document.createElement('iframe');
    const lang = getLocale() === 'kk' ? 'kz' : getLocale();
    const query = new URLSearchParams({
      platform: 'tv',
      topic: this.topicId === null ? 'offline' : String(this.topicId),
      difficulty: this.difficulty,
      lang,
    });
    frame.src = `${PAGE_URL}?${query.toString()}`;
    frame.setAttribute('scrolling', 'no');
    frame.style.cssText = `position:absolute;border:0;z-index:1;background:${PAGE_COLOR};`;
    this.host.appendChild(frame);
    this.frame = frame;
    this.pageReady = false;
    this.pageStarted = false;
    this.fit();
    window.addEventListener('message', this.onMessage);
    window.clearTimeout(this.loadTimer);
    this.loadTimer = window.setTimeout(() => {
      if (!this.pageReady) this.failLoad();
    }, LOAD_TIMEOUT_MS);
  }

  private destroyPage(): void {
    window.clearTimeout(this.loadTimer);
    window.removeEventListener('message', this.onMessage);
    this.frame?.remove();
    this.frame = null;
    this.pageReady = false;
    this.loadFailed = false;
    this.startPending = false;
    this.pageStarted = false;
  }

  /**
   * Телефон прислал выбор: страница читает его при загрузке, поэтому поднимаем её (или пересобираем под другой).
   * Фаза остаётся: если раунд уже идёт, новый стартует сразу.
   */
  private applySetup(frame: IqFrame): void {
    const difficulty = frame.difficulty ?? this.difficulty;
    const topicId = frame.topicId === undefined ? this.topicId : frame.topicId;
    const changed = this.setupKnown
      && setupChanged({ topicId: this.topicId, difficulty: this.difficulty }, { topicId, difficulty });
    this.topicId = topicId;
    this.difficulty = difficulty;
    this.setupKnown = true;
    if (this.phase === 'lobby') return;
    if (!this.frame) {
      this.ensurePage();
      return;
    }
    if (!changed) return;
    // Кадр подготовки при паузе — телефон в калибровке: новый раунд начнётся по `start-game`, а не сам. Но если
    // `start-game` уже был (фаза `playing`), а страница пересобирается, стартовать её надо, иначе она так и повиснет.
    const wasPaused = this.phase === 'paused';
    const shouldStart = startsAfterRebuild(this.phase, frame.gameState);
    this.destroyPage();
    this.ensurePage();
    if (shouldStart) {
      this.showStatus(wasPaused ? translate('game.pause') : null);
      this.startPending = true;
    }
  }

  /** Самый большой прямоугольник 16:9, который помещается в экран, по центру. */
  private readonly fit = (): void => {
    const { frame } = this;
    if (!frame) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.floor(Math.min(vw, (vh * 16) / 9));
    const h = Math.floor((w * 9) / 16);
    frame.style.width = `${w}px`;
    frame.style.height = `${h}px`;
    frame.style.left = `${Math.floor((vw - w) / 2)}px`;
    frame.style.top = `${Math.floor((vh - h) / 2)}px`;
  };

  private readonly onMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin || event.source !== this.frame?.contentWindow) return;
    const { data } = event as { data: unknown };
    const message = data && typeof data === 'object' ? (data as Record<string, unknown>).groove : undefined;
    if (!message || typeof message !== 'object') return;
    const {
      type, score, correct, total, durationSec, message: text,
    } = message as Record<string, unknown>;

    if (type === 'ready') {
      this.pageReady = true;
      this.loadFailed = false;
      window.clearTimeout(this.loadTimer);
      // Страница ответила после таймаута — плашка «не удалось загрузить» уже на экране.
      if (this.phase !== 'paused' && this.phase !== 'gameover') this.showStatus(null);
      if (this.startPending) {
        this.startPage();
        // Пересобранная под паузой (отвал телефона, пауза игрока) страница не должна бежать под плашкой.
        if (this.phase === 'paused') this.api()?.groovePause();
      }
    } else if (type === 'finished') {
      const result: IqWebResult = {
        score: count(score), correct: count(correct), total: count(total), durationSec: count(durationSec),
      };
      this.phase = 'gameover';
      // В конце раунда на странице пусто — итог рисуем поверх.
      this.showStatus(translate('game.iq_over', {
        score: result.score, correct: result.correct, total: result.total,
      }));
      this.onFinished?.(result);
    } else if (type === 'error') {
      // eslint-disable-next-line no-console
      console.warn('[IqWeb] page error', text);
      // Страница, которая сломалась до `ready` (нет вопросов, сервер не отвечает), сама не оживёт: говорим сразу.
      if (!this.pageReady) this.failLoad();
    }
  };

  private failLoad(): void {
    this.loadFailed = true;
    this.showStatus(translate('game.chef_load_failed'));
  }

  private startPage(): void {
    if (this.pageStarted) return;
    const page = this.api();
    if (!page) {
      this.startPending = true;
      return;
    }
    this.startPending = false;
    this.pageStarted = true;
    page.grooveStart();
  }

  /** Окно страницы, когда она загрузилась и сказала `ready`; иначе `null`. */
  private api(): IqPageApi | null {
    if (!this.pageReady) return null;
    return (this.frame?.contentWindow as unknown as IqPageApi | null | undefined) ?? null;
  }

  private showStatus(text: string | null): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = text ?? '';
    this.statusEl.style.display = text ? 'block' : 'none';
  }

  // ─── Калибровка ───────────────────────────────────────────────────────────

  /** Экран «встаньте в зону»: калибровка идёт на телефоне, ТВ рисует рамку по её прогрессу. */
  private showCalibration(visible: boolean): void {
    cancelAnimationFrame(this.calibRaf);
    this.calibRaf = 0;
    if (!this.calibCanvas) return;
    this.calibCanvas.style.display = visible ? 'block' : 'none';
    if (!visible) return;
    const tick = (): void => {
      if (this.phase !== 'calibration') return;
      this.drawCalibration();
      this.calibRaf = requestAnimationFrame(tick);
    };
    tick();
  }

  private drawCalibration(): void {
    const canvas = this.calibCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;
    drawUpperBodyCalibration(ctx, W, H, this.detectionProgress);
  }
}
